(() => {
  const ROOT_SELECTOR = '.article-template__content :is(.ecom-article, .ecom-article-wrapper, #ecom-article-wrapper, .ecom-article-container, #ecom-article-container)';
  const ARTICLE_CONTENT_SELECTOR = '.article-template__content';
  const ARTICLE_LINK_SELECTOR = 'a[href]';
  const DEFAULT_DETAILS_SELECTOR = 'details:not([data-default-collapsed])';
  const NON_DESTINATION_HREF = /^(?:#|mailto:|tel:|javascript:|data:|blob:)/i;
  const UNSUPPORTED_HREF_SCHEME = /^(?!https?:)[a-z][a-z\d+.-]*:/i;
  // This is an open capability vocabulary: registered markers opt an element
  // into shared behavior, but article-specific sections/components may still
  // use their own semantic markup and ecom-x-* classes.
  const CAPABILITY_REGISTRY = Object.freeze({
    checklist: '[data-ecom-checklist], [data-ecom-capability~="checklist"], .ecom-checklist, .ecom-x-checklist',
    choice: '[data-ecom-choice], [data-ecom-capability~="choice"], [data-ecom-triage-result]',
    reveal: '[data-ecom-reveal], [data-ecom-capability~="reveal"]',
    toggle: '[data-ecom-toggle], [data-ecom-capability~="toggle"]',
    choiceGroup: '[data-ecom-choice-group], [data-ecom-capability~="choices"], [data-ecom-capability~="choice-group"], .ecom-choice-group',
  });
  const CHECKLIST_SELECTOR = CAPABILITY_REGISTRY.checklist;
  const CHOICE_SELECTOR = CAPABILITY_REGISTRY.choice;
  const ROOT_CHOICE_SELECTOR = `${ROOT_SELECTOR} [data-ecom-choice], ${ROOT_SELECTOR} [data-ecom-capability~="choice"]`;
  const ROOT_TRIAGE_CHOICE_SELECTOR = `${ROOT_SELECTOR} [data-ecom-triage-result]`;
  const REVEAL_SELECTOR = CAPABILITY_REGISTRY.reveal;
  const TOGGLE_SELECTOR = CAPABILITY_REGISTRY.toggle;
  const ROOT_TOGGLE_SELECTOR = `${ROOT_SELECTOR} [data-ecom-toggle], ${ROOT_SELECTOR} [data-ecom-capability~="toggle"]`;
  const CHOICE_GROUP_SELECTOR = CAPABILITY_REGISTRY.choiceGroup;
  const COPY_SOURCE_SELECTOR = '[data-ecom-copy-source], [data-ecom-region~="summary"], [data-ecom-capability~="summary"], .ecom-summary, .ecom-key-takeaways, .ecom-decision-card';
  const KNOWN_ACTIONS = new Set(['print', 'copy-summary']);

  const findRoot = (element) => element?.closest?.(ROOT_SELECTOR);
  const findChoiceGroup = (choice) =>
    choice?.closest?.(CHOICE_GROUP_SELECTOR) ||
    choice?.closest?.('[data-ecom-interaction-id], [data-ecom-custom-module]');

  const getHintId = (index) => {
    const baseId = `ecom-new-tab-hint-${index}`;
    let hintId = baseId;
    let suffix = 2;
    while (document.getElementById?.(hintId)) {
      hintId = `${baseId}-${suffix}`;
      suffix += 1;
    }
    return hintId;
  };

  const addNewTabHint = (link, index, hintText) => {
    if (!hintText) return;
    let hint = link.querySelector?.('[data-ecom-new-tab-hint]');
    if (!hint) {
      hint = document.createElement('span');
      hint.className = 'visually-hidden';
      hint.dataset.ecomNewTabHint = 'true';
      hint.id = getHintId(index);
      hint.textContent = ` ${hintText}`;
      link.append(hint);
    }

    if (!hint.id) hint.id = getHintId(index);
    const describedBy = new Set((link.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    describedBy.add(hint.id);
    link.setAttribute('aria-describedby', [...describedBy].join(' '));
  };

  const normalizeArticleLinks = (content) => {
    if (!content?.querySelectorAll) return;
    const hintText = content.dataset?.ecomNewWindowLabel?.trim() || '';

    content.querySelectorAll(ARTICLE_LINK_SELECTOR).forEach((link, index) => {
      if (link.dataset.ecomLinkNormalized === 'true') return;
      const href = (link.getAttribute('href') || '').trim();
      if (!href || link.hasAttribute('download') || NON_DESTINATION_HREF.test(href) || UNSUPPORTED_HREF_SCHEME.test(href)) return;

      link.setAttribute('target', '_blank');
      const relTokens = (link.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
      if (!relTokens.some((token) => token.toLowerCase() === 'noopener')) relTokens.push('noopener');
      link.setAttribute('rel', relTokens.join(' '));
      addNewTabHint(link, index, hintText);
      link.dataset.ecomLinkNormalized = 'true';
    });
  };

  const findRevealPanel = (control, root) => {
    const targetId = control.getAttribute?.('aria-controls')?.trim();
    if (!targetId || !root?.querySelectorAll) return null;
    return Array.from(root.querySelectorAll(REVEAL_SELECTOR)).find((panel) => panel.id === targetId) || null;
  };

  const toggleReveal = (control, root) => {
    const tagName = control.tagName?.toLowerCase();
    const role = control.getAttribute?.('role');
    if (tagName !== 'button' && role !== 'button') return false;

    const panel = findRevealPanel(control, root);
    if (!panel) return false;

    const currentlyHidden = panel.hidden === true || panel.hasAttribute?.('hidden');
    const willOpen = currentlyHidden;
    panel.hidden = !willOpen;
    if (willOpen) panel.removeAttribute?.('hidden');
    else panel.setAttribute?.('hidden', '');
    control.setAttribute('aria-expanded', String(willOpen));
    return true;
  };

  const setStatus = (root, message) => {
    const status = root.querySelector('[data-ecom-action-status]');
    if (!status) return;
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.textContent = message;
  };

  const updateChecklist = (checklist) => {
    const checkboxes = Array.from(checklist.querySelectorAll('input[type="checkbox"]'));
    if (!checkboxes.length) return;

    const completed = checkboxes.filter((checkbox) => checkbox.checked).length;
    let status = checklist.querySelector('.ecom-checklist__status');
    if (!status) {
      status = document.createElement('p');
      status.className = 'ecom-checklist__status';
      checklist.append(status);
    }
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.textContent = `${completed} of ${checkboxes.length} completed`;

    checklist.dataset.ecomCompleted = String(completed);
    checklist.dataset.ecomTotal = String(checkboxes.length);
  };

  const initializeDetails = (root) => {
    root.querySelectorAll(DEFAULT_DETAILS_SELECTOR).forEach((details) => {
      details.open = true;
    });
  };

  const initializeRoot = (root) => {
    if (root.dataset.ecomEnhanced === 'true') return;
    root.dataset.ecomEnhanced = 'true';

    initializeDetails(root);
    root.querySelectorAll(CHECKLIST_SELECTOR).forEach(updateChecklist);
    root.querySelectorAll(CHOICE_SELECTOR).forEach((choice) => {
      if (!choice.hasAttribute('aria-pressed')) choice.setAttribute('aria-pressed', 'false');
    });
    root.querySelectorAll(TOGGLE_SELECTOR).forEach((toggle) => {
      const panel = findRevealPanel(toggle, root);
      if (panel && !toggle.hasAttribute('aria-expanded')) {
        const isHidden = panel.hidden === true || panel.hasAttribute?.('hidden');
        toggle.setAttribute('aria-expanded', String(!isHidden));
      }
    });
  };

  const copySummary = async (button, root) => {
    const localSource = button.closest('[data-ecom-copy-source], .ecom-summary, .ecom-key-takeaways, .ecom-decision-card') || button.closest(COPY_SOURCE_SELECTOR);
    const summary = localSource || root.querySelector(COPY_SOURCE_SELECTOR);
    const cleanSource = summary?.cloneNode?.(true);
    cleanSource?.querySelectorAll?.('.ecom-component__actions, [data-ecom-capability~="actions"], [data-ecom-action-status], [data-ecom-new-tab-hint], button').forEach((element) => element.remove());
    const textSource = cleanSource?.innerText || cleanSource?.textContent || '';
    const text = textSource.replace(/\n{3,}/g, '\n\n').trim();
    if (!text) {
      setStatus(root, 'There is no summary to copy.');
      return;
    }

    try {
      const clipboard = globalThis.navigator?.clipboard;
      if (!clipboard || typeof clipboard.writeText !== 'function') throw new Error('clipboard-unavailable');
      await clipboard.writeText(text);
      setStatus(root, 'Summary copied.');
      button.dataset.ecomActionState = 'complete';
    } catch (error) {
      setStatus(root, 'Copy is unavailable in this browser.');
      button.dataset.ecomActionState = 'unavailable';
    }
  };

  document.addEventListener('change', (event) => {
    const checkbox = event.target.closest?.(`${ROOT_SELECTOR} input[type="checkbox"]`);
    if (!checkbox) return;
    const checklist = checkbox.closest(CHECKLIST_SELECTOR);
    if (checklist) updateChecklist(checklist);
  });

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest?.(ROOT_TOGGLE_SELECTOR);
    if (toggle) {
      if (toggle.disabled || toggle.getAttribute?.('aria-disabled') === 'true') return;
      const root = findRoot(toggle);
      if (root && toggleReveal(toggle, root)) {
        event.preventDefault?.();
        return;
      }
    }

    const choice = event.target.closest?.(`${ROOT_CHOICE_SELECTOR}, ${ROOT_TRIAGE_CHOICE_SELECTOR}`);
    if (choice) {
      if (choice.disabled || choice.getAttribute?.('aria-disabled') === 'true') return;
      const root = findRoot(choice);
      const group = findChoiceGroup(choice);
      const choices = group ? group.querySelectorAll(CHOICE_SELECTOR) : [choice];
      choices.forEach((candidate) => candidate.setAttribute('aria-pressed', String(candidate === choice)));
      if (root) setStatus(root, `Selected: ${choice.textContent.trim()}`);
      return;
    }

    const action = event.target.closest?.(`${ROOT_SELECTOR} [data-ecom-action]`);
    if (!action) return;
    const root = findRoot(action);
    if (!root) return;
    if (action.disabled || action.getAttribute?.('aria-disabled') === 'true') return;

    const actionName = action.dataset.ecomAction;
    if (!KNOWN_ACTIONS.has(actionName)) return;

    if (action.dataset.ecomAction === 'print' && actionName === 'print') {
      if (typeof globalThis.window?.print === 'function') {
        globalThis.window.print();
      } else {
        setStatus(root, 'Print is unavailable in this browser.');
      }
      return;
    }

    if (action.dataset.ecomAction === 'copy-summary' && actionName === 'copy-summary') {
      copySummary(action, root);
    }
  });

  const initialize = (scope = document) => {
    scope.querySelectorAll(ARTICLE_CONTENT_SELECTOR).forEach(normalizeArticleLinks);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initializeRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target || document));
})();
